import { ArrowRight, Check, Search } from 'lucide-react';
import Screen from '../../demo/Screen';
import { AssistantMark, DocsMark, GoogleMark } from '../../mocks/BrandMarks';
import { DoctorFigure, PatientFigure } from './ShiftFigures';
import './PatientsResearch.css';

const TESTS = ['CBC', 'Lipid Profile', 'Thyroid', 'Vitamin D', 'Liver Function Test'];

const SUGGESTIONS = [
  'Tests for fatigue',
  'Full body checkup near me',
  'Best diagnostic lab in my area',
  'Difference between CBC and KFT',
];

/*
 * Where patients look things up: Google search, a Google Docs article, a map
 * search and ChatGPT, each shown by its mark (decorative) and a two-line label
 * (broken where the mockup breaks it).
 */

/* The mockup's map search mark: a solid blue location pin. */
function PinMark() {
  return (
    <svg width="30" height="34" viewBox="0 0 30 34" aria-hidden="true" focusable="false">
      <path d="M15 1C7.5 1 1.5 6.9 1.5 14.3 1.5 23.5 15 33 15 33s13.5-9.5 13.5-18.7C28.5 6.9 22.5 1 15 1Z" fill="#1a5ad8" />
      <circle cx="15" cy="14" r="5" fill="#fff" />
    </svg>
  );
}

const SOURCES_LEFT = [
  { key: 'search', mark: <GoogleMark size={30} />, text: ['Blood tests', 'for fatty liver'] },
  { key: 'article', mark: <DocsMark size={32} />, text: ['Symptoms', 'and tests'] },
];

const SOURCES_RIGHT = [
  { key: 'maps', mark: <PinMark />, text: ['Diagnostic', 'labs near me'] },
  {
    key: 'assistant',
    mark: (
      <span className="research-src__ai">
        <AssistantMark size={23} color="#fff" />
      </span>
    ),
    text: ['Which tests', 'do I need?'],
  },
];

function SourceCard({ mark, text, side }) {
  return (
    <li className={`research-src research-src--${side}`}>
      <span className="research-src__icon">{mark}</span>
      <span className="research-src__text">
        {text[0]} <span className="research-br">{text[1]}</span>
      </span>
      <span className="research-src__bar" />
      <span className="research-src__bar research-src__bar--short" />
    </li>
  );
}

function PersonGlyph() {
  return (
    <svg className="research-done__icon" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <circle cx="24" cy="14" r="9" fill="currentColor" />
      <path d="M6 44c0-10 8-17 18-17s18 7 18 17Z" fill="currentColor" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 60 120" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="research-chevron" x1="0" y1="0" x2="1" y2="0.6">
          <stop offset="0" stopColor="#eaf1ff" />
          <stop offset="0.6" stopColor="#9fc0ff" />
          <stop offset="1" stopColor="#5b93ff" />
        </linearGradient>
      </defs>
      <path d="M6 4h16l34 56-34 56H6l30-56Z" fill="url(#research-chevron)" />
    </svg>
  );
}

export default function PatientsResearch() {
  return (
    <Screen id="shift.patients-research" title="What it fixes for you" className="research">
      <p className="eyebrow research-eyebrow">Shift · 1 of 2</p>

      <div className="research-compare">
        <section className="research-panel research-panel--earlier" aria-labelledby="research-earlier">
          <DoctorFigure className="research-figure research-figure--doctor" />
          <span className="research-pill">Earlier</span>
          <h2 id="research-earlier">The doctor decided which tests to do.</h2>
          <p className="research-panel__lede">Patients typically followed the doctor’s advice.</p>

          <div className="research-earlier" aria-hidden="true">
            <div className="research-rx">
              <p className="research-rx__title">Tests Recommended</p>
              <ul>
                {TESTS.map((t) => (
                  <li key={t}>
                    <span className="research-rx__box">
                      <Check size={12} strokeWidth={3.4} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <ArrowRight className="research-earlier__arrow" size={36} strokeWidth={2.2} />
            <div className="research-done">
              <p>
                Patient <span className="research-br">gets the tests</span> <span className="research-br">done</span>
              </p>
              <PersonGlyph />
              <span className="research-done__bar" />
              <span className="research-done__bar research-done__bar--short" />
            </div>
          </div>
        </section>

        <span className="research-chevron" aria-hidden="true">
          <Chevron />
        </span>

        <section className="research-panel research-panel--now" aria-labelledby="research-now">
          <PatientFigure className="research-figure research-figure--patient" />
          <span className="research-pill">Now</span>
          <h2 id="research-now">Patients use the internet more than ever before to make healthcare decisions.</h2>
          <p className="research-panel__lede">
            They research symptoms, compare tests, read about health conditions, check labs and packages, and
            often arrive with questions.
          </p>

          <div className="research-now" aria-hidden="true">
            <ul className="research-col">
              {SOURCES_LEFT.map(({ key, ...s }) => (
                <SourceCard key={key} side="left" {...s} />
              ))}
            </ul>
            <div className="research-search">
              <span className="research-search__box">
                <Search size={16} strokeWidth={2.6} />
                Which blood tests do I need?
              </span>
              <ul>
                {SUGGESTIONS.map((s) => (
                  <li key={s}>
                    <Search size={14} strokeWidth={2.4} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <ul className="research-col">
              {SOURCES_RIGHT.map(({ key, ...s }) => (
                <SourceCard key={key} side="right" {...s} />
              ))}
            </ul>
          </div>
        </section>
      </div>

      <h1 className="headline research-headline">
        The doctor used to decide. <em>Now patients research and compare.</em>
      </h1>
    </Screen>
  );
}
